/** @file Store for tar archives generated by AutoTest v2 Output steps. */
import { Maybe } from '@codegrade/maybe';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import * as utils from '@/utils';
import * as fileUtils from '@/utils/files';
const storeBuilder = getStoreBuilder();
/** @returns The initial state of this store. */
const makeInitialState = () => ({
    loaders: {},
    tarFiles: {},
});
const moduleBuilder = storeBuilder.module('atv2_output', makeInitialState());
/** The interface of this store module. */
export var ATv2OutputStore;
(function (ATv2OutputStore) {
    /**
     * Get a key for the tar file mapping.
     *
     * @param programId The SAFE program id.
     * @param stepId The Output step id.
     * @returns The key to use in the tar file mapping.
     */
    function makeKey(programId, stepId) {
        return `${programId}/${stepId}`;
    }
    const commitTarFile = moduleBuilder.commit((state, args) => {
        utils.vueSet(state.tarFiles, args.key, args.tarFile);
    }, 'commitTarFile');
    const commitLoader = moduleBuilder.commit((state, args) => {
        args.loader.caseOf({
            Nothing: () => {
                utils.vueDelete(state.loaders, args.key);
            },
            Just: loader => {
                utils.vueSet(state.loaders, args.key, loader);
            },
        });
    }, 'commitLoader');
    /** Get the tar file for the given step, if it has already been downloaded. */
    ATv2OutputStore.getTarFile = moduleBuilder.read(state => 
    /**
     * Get the tar file for the given step, if it has already been downloaded.
     *
     * @param programId The id of the SAFE program that created the output.
     * @param stepId The id of the step to get the tar file for.
     * @returns The tar file for the given step id, if it has already been downloaded.
     */
    (programId, stepId) => {
        const key = makeKey(programId, stepId);
        return Maybe.fromNullable(state.tarFiles[key]);
    }, 'getTarFile');
    /** Download the tar file for the given step, if it hasn't been already. */
    ATv2OutputStore.loadTarFile = moduleBuilder.dispatch(async ({ state }, args) => {
        const key = makeKey(args.programId, args.stepId);
        return await utils.storeLoadWithMaybeForce({
            value: Maybe.fromNullable(state.tarFiles[key]),
            loader: Maybe.fromNullable(state.loaders[key]),
            force: args.force === true,
            load: async () => {
                const [tar, data] = await Promise.all([
                    import('@codegrade/tarfile'),
                    fileUtils.downloadCompressedFileFromS3(args.loc),
                ]);
                const tarFile = tar.TarFile.create(data).unsafeCoerce();
                commitTarFile({ key, tarFile });
                return tarFile;
            },
            addLoader: loader => commitLoader({ loader: Maybe.of(loader), key }),
            removeLoader: () => commitLoader({ loader: Maybe.empty(), key }),
        });
    }, 'loadTarFile');
    const commitClearAll = moduleBuilder.commit(state => {
        utils.vueSet(state, 'tarFiles', {});
        utils.vueSet(state, 'loaders', {});
    }, 'commitClearAll');
    utils.signal.AFTER_USER_CHANGED.register(async () => {
        commitClearAll();
    });
})(ATv2OutputStore || (ATv2OutputStore = {}));
